import chunk from 'lodash/chunk';

const DEFAULT_CHUNK_SIZE = 10;

export type ChunkFailureMode = 'abort-batch' | 'isolate';

interface RunInChunksOptions<T> {
  chunkSize?: number;
  failureMode?: ChunkFailureMode;
  onError?: (error: Error, item?: T) => void;
}

// 'abort-batch' stops at the first failing chunk (Promise.all can't say which item threw);
// 'isolate' runs every item and reports failures with the item, so none can block the rest.
export async function runInChunks<T>(
  items: Array<T>,
  fn: (item: T) => Promise<unknown>,
  { chunkSize = DEFAULT_CHUNK_SIZE, failureMode = 'abort-batch', onError }: RunInChunksOptions<T> = {},
): Promise<void> {
  for (const batch of chunk(items, chunkSize)) {
    if (failureMode === 'isolate') {
      await Promise.all(
        batch.map(async (item) => {
          try {
            await fn(item);
          } catch (error) {
            onError?.(error instanceof Error ? error : new Error(String(error)), item);
          }
        }),
      );
      continue;
    }

    try {
      await Promise.all(batch.map(fn));
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error(String(error)));
      return;
    }
  }
}

export async function deleteAllInChunks(
  ids: Array<string>,
  deleteFn: (id: string) => Promise<unknown>,
  onError: (error: Error) => void,
  chunkSize = DEFAULT_CHUNK_SIZE,
): Promise<void> {
  await runInChunks(ids, deleteFn, { chunkSize, failureMode: 'abort-batch', onError });
}
